import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Form, Label } from "semantic-ui-react";

import { OrgStructures } from "@teamprevent/common/orgStructure/components/OrgStructures";
import { OrgStructuresModel } from "@teamprevent/common/orgStructure/models";
import { LinkObject } from "@teamprevent/common/model";

import { CustomerListModel, CustomerState } from "../../customer";
import { loadOrgStructures } from "../../orgStructure";
import { WorkGroup, loadWorkGroups } from "../../workGroup";
import { workGroupsSelector } from "../../workGroup/selectors";

import { AppState } from "../model";
import { EmployeeRecordFormModel } from "../schema";

interface Props {
    initialModel?: EmployeeRecordFormModel;
    customer: CustomerListModel;
}

export const CreateEditEmployee = ({ initialModel, customer }: Props) => {
    const dispatch = useDispatch();
    const workGroups: WorkGroup[] = useSelector((state: AppState & CustomerState) => workGroupsSelector(state));
    const { register, formState: { errors }, watch, control, setValue } = useFormContext<EmployeeRecordFormModel>();
    const secondaryReference = watch("secondaryReference");

    register("customerId", { value: customer.id });
    register("customerName", { value: customer.name });

    const isDobMandatory = React.useCallback(() => {
        return !(secondaryReference ?? "").trim();
    }, [secondaryReference]);

    const loadOrgStructure = React.useCallback(async (parentOrgStructureId: string) => {
        await dispatch(loadOrgStructures({ customerId: customer.id, parentOrgStructureId }));
    }, [customer.id, dispatch]);

    const onOrgStructuresModelChange = React.useCallback((orgStructuresModel: OrgStructuresModel) => {
        if (orgStructuresModel?.orgStructureOne)
        {
            setValue("orgStructureOne", orgStructuresModel.orgStructureOne, { shouldValidate: true });
        } else {
            setValue("orgStructureOne", undefined as unknown as LinkObject);
        }

        setValue("orgStructureTwo", orgStructuresModel?.orgStructureTwo ?? undefined);
        setValue("orgStructureThree", orgStructuresModel?.orgStructureThree ?? undefined);
        setValue("orgStructureFour", orgStructuresModel?.orgStructureFour ?? undefined);
    }, [setValue]);

    React.useEffect(() => {
        if (workGroups.length === 0) {
            dispatch(loadWorkGroups({ customerId: customer.id }));
        }
    }, [customer.id, dispatch, workGroups.length]);

    return (
        <>
            <Form.Field>
                {errors.secondaryReference?.message && (<Label pointing="below" color="red">{errors.secondaryReference.message}</Label>)}
                <label>Employee Unique ID</label>
                <input {...register("secondaryReference")} />
            </Form.Field>
            <Form.Field>
                {errors.title?.message && (<Label pointing="below" color="red">{errors.title.message}</Label>)}
                <label>Title *</label>
                <input {...register("title")} />
            </Form.Field>
            <Form.Field>
                {errors.firstName?.message && (<Label pointing="below" color="red">{errors.firstName.message}</Label>)}
                <label>First Name *</label>
                <input {...register("firstName")} />
            </Form.Field>
            <Form.Field>
                {errors.lastName?.message && (<Label pointing="below" color="red">{errors.lastName.message}</Label>)}
                <label>Last Name *</label>
                <input {...register("lastName")} />
            </Form.Field>
            <Form.Field>
                {errors.previousLastName?.message && (<Label pointing="below" color="red">{errors.previousLastName.message}</Label>)}
                <label>Previous Last Name</label>
                <input {...register("previousLastName")} />
            </Form.Field>
            <Form.Field>
                {errors.gender?.message && (<Label pointing="below" color="red">{errors.gender.message}</Label>)}
                <Controller
                    name="gender"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field: { onChange, value } }) =>
                        (
                            <>
                                <label>Gender</label>
                                <Form.Dropdown
                                    selection
                                    placeholder="Please select"
                                    name="gender"
                                    options={GENDER_OPTIONS}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onChange={(e, data) => onChange(data.value)}
                                    value={value ?? undefined}
                                />
                            </>
                        )
                    }
                />
            </Form.Field>
            <Form.Field>
                {errors.dateOfBirth?.message && (<Label pointing="below" color="red">{errors.dateOfBirth.message}</Label>)}
                <label>{"Date Of Birth" + (isDobMandatory() ? " *" : "")}</label>
                <input
                    type="date"
                    {...register("dateOfBirth")}
                />
            </Form.Field>
            {errors.orgStructureOne?.message && (<Label pointing="below" color="red">{errors.orgStructureOne.message}</Label>)}
            <OrgStructures
                onOrgStructuresModelChange={onOrgStructuresModelChange}
                loadOrgStructure={loadOrgStructure}
                initialModel={initialModel as OrgStructuresModel}
                customerId={customer.id}
            />
            <br />
            <Form.Field>
                {errors.gender?.message && (<Label pointing="below" color="red">{errors.gender.message}</Label>)}
                <Controller
                    name="workGroups"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field: { onChange, value } }) =>
                    {
                        return (
                            <>
                                <label>Work Groups</label>
                                <Form.Dropdown
                                    selection
                                    multiple
                                    search
                                    fluid
                                    placeholder="Please select"
                                    name="workGroups"
                                    options={workGroups.map(workGroup => ({ value: workGroup.id, text: workGroup.name }))}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onChange={(e, data) => {
                                        if (Array.isArray(data.value)) {
                                            const newData: LinkObject[] = [];
                                            (data.value.map(v => workGroups.find(item => item.id === v))).forEach(element => {
                                                if (element)
                                                {
                                                    newData.push({ id: element.id, name: element.name });
                                                }
                                            });
                                            onChange(newData);
                                        }
                                    }}
                                    value={Array.isArray(value) ? value.map(v => v.id ) : []}
                                />
                            </>
                        );
                    }
                    }
                />
            </Form.Field>
            <Form.Field>
                {errors.personalEmailAddress?.message && (<Label pointing="below" color="red">{errors.personalEmailAddress.message}</Label>)}
                <label>Personal Email Address</label>
                <input type="email" {...register("personalEmailAddress")} />
            </Form.Field>
            <Form.Field>
                {errors.workEmailAddress?.message && (<Label pointing="below" color="red">{errors.workEmailAddress.message}</Label>)}
                <label>Work Email Address</label>
                <input type="email" {...register("workEmailAddress")} />
            </Form.Field>
            <Form.Field>
                {errors.homePhoneNumber?.message && (<Label pointing="below" color="red">{errors.homePhoneNumber.message}</Label>)}
                <label>Home Phone Number</label>
                <input {...register("homePhoneNumber")} />
            </Form.Field>
            <Form.Field>
                {errors.workPhoneNumber?.message && (<Label pointing="below" color="red">{errors.workPhoneNumber.message}</Label>)}
                <label>Work Phone Number</label>
                <input {...register("workPhoneNumber")} />
            </Form.Field>
            <Form.Field>
                {errors.mobilePhoneNumber?.message && (<Label pointing="below" color="red">{errors.mobilePhoneNumber.message}</Label>)}
                <label>Mobile Phone Number</label>
                <input {...register("mobilePhoneNumber")} />
            </Form.Field>
            <Form.Field>
                {errors.addressLine1?.message && (<Label pointing="below" color="red">{errors.addressLine1.message}</Label>)}
                <label>Address Line1</label>
                <input {...register("addressLine1")} />
            </Form.Field>
            <Form.Field>
                {errors.addressLine2?.message && (<Label pointing="below" color="red">{errors.addressLine2.message}</Label>)}
                <label>Address Line2</label>
                <input {...register("addressLine2")} />
            </Form.Field>
            <Form.Field>
                {errors.addressLine3?.message && (<Label pointing="below" color="red">{errors.addressLine3.message}</Label>)}
                <label>Address Line3</label>
                <input {...register("addressLine3")} />
            </Form.Field>
            <Form.Field>
                {errors.city?.message && (<Label pointing="below" color="red">{errors.city.message}</Label>)}
                <label>City</label>
                <input {...register("city")} />
            </Form.Field>
            <Form.Field>
                {errors.county?.message && (<Label pointing="below" color="red">{errors.county.message}</Label>)}
                <label>County</label>
                <input {...register("county")} />
            </Form.Field>
            <Form.Field>
                {errors.postCode?.message && (<Label pointing="below" color="red">{errors.postCode.message}</Label>)}
                <label>Post Code</label>
                <input {...register("postCode")} />
            </Form.Field>
            <Form.Field>
                {errors.jobTitle?.message && (<Label pointing="below" color="red">{errors.jobTitle.message}</Label>)}
                <label>Job Title</label>
                <input {...register("jobTitle")} />
            </Form.Field>
            <Form.Field>
                {errors.criticalNotes?.message && (<Label pointing="below" color="red">{errors.criticalNotes.message}</Label>)}
                <label>Critical Notes</label>
                <input {...register("criticalNotes")} />
            </Form.Field>
            <Form.Field>
                {errors.terminationDate?.message && (<Label pointing="below" color="red">{errors.terminationDate.message}</Label>)}
                <label>Termination Date</label>
                <input
                    type="date"
                    {...register("terminationDate")}
                />
            </Form.Field>
            <Form.Field>
                <label>Customer</label>
                <span>{customer.name}</span>
            </Form.Field>
        </>
    );
};
